/**
 * Pure view logic for partner screens. The server is authoritative for every rule; these helpers only decide
 * what the screen shows and which actions it offers, mirroring the shared contract helpers.
 */
import { DELIVERABLE_TRANSITIONS, MANUAL_TARGETS, type DeliverableState } from '@qm/contracts/deliverables';
import { AGENCY_LLM_ALLOWLIST, checkAccent, descriptorProblem, impersonatesQuixo, BrandSettingsSchema, type HealthBand } from '@qm/contracts/workspace';
import { PartnerApplicationSchema, PARTNER_PRIVACY_NOTICE_VERSION, PARTNER_TERMS_VERSION, type PartnerStatus } from '@qm/contracts/partners';
import { MCP_SCOPES, type McpScope } from '@qm/contracts/mcp';
import type { Tone } from '../kit/ui';
import type { Tone as PastelTone } from '@qm/ui';
import { errorInfo } from '../kit/http';
import type { ClientStatus, ClientView, DomainView, McpKeyView, WaUsageView, WeekItem, DeliverableView, ApprovalView, MonthlyReportView } from '../api';

// ------------------------------------------------------------------ gate (B09-002, C04-002)
export type Gate = 'open' | 'pending' | 'rejected' | 'suspended' | 'signed_out';
export function gateFor(me: { status: PartnerStatus; locked: boolean } | null, err?: unknown): Gate {
  if (err) {
    const e = errorInfo(err);
    if (e.status === 401) return 'signed_out';
    if (e.reason === 'partner_suspended') return 'suspended';
    if (e.reason === 'partner_rejected') return 'rejected';
    if (e.reason === 'partner_pending') return 'pending';
    return 'signed_out';
  }
  if (!me) return 'signed_out';
  if (me.status === 'approved' && !me.locked) return 'open';
  if (me.status === 'suspended') return 'suspended';
  if (me.status === 'rejected') return 'rejected';
  return 'pending';
}

// ------------------------------------------------------------------ apply (B09-001)
export interface ApplyForm {
  companyName: string; website: string; country: string; city: string; designation: string;
  contactName: string; contactEmail: string; contactPhone: string; clientCount: string; acceptTerms: boolean;
  /** Legacy callers only; not shown or submitted by this form. */
  companyDomain?: string; registrationNumber?: string; clientCountBand?: string;
}
export const emptyApplyForm = (): ApplyForm => ({ companyName:'',website:'',country:'AE',city:'',designation:'',contactName:'',contactEmail:'',contactPhone:'',clientCount:'',acceptTerms:false });
export function buildApplication(f: ApplyForm) {
  const errors:Record<string,string>={};
  for(const field of ['city','designation','contactPhone'] as const)if(!f[field]?.trim())errors[field]='invalid';
  if(!/^[0-9]+$/.test(f.clientCount.trim()) || Number(f.clientCount)>1000000)errors.clientCount='invalid';
  const raw={companyName:f.companyName,country:f.country,city:f.city,designation:f.designation,contactName:f.contactName,contactEmail:f.contactEmail,contactPhone:f.contactPhone,clientCount:Number(f.clientCount),...(f.website.trim()?{website:f.website.trim()}:{}),services:[],acceptTerms:f.acceptTerms,termsVersion:PARTNER_TERMS_VERSION,privacyNoticeVersion:PARTNER_PRIVACY_NOTICE_VERSION};
  const parsed=PartnerApplicationSchema.safeParse(raw);
  if(!parsed.success)for(const issue of parsed.error.issues){const key=String(issue.path[0]??'form');errors[key]??=key==='acceptTerms'?'terms':'invalid';}
  if(parsed.success&&!Object.keys(errors).length)return {ok:true as const,value:parsed.data,errors};
  return {ok:false as const,value:null,errors};
}
/** Duplicate application for the domain -> "under review" message rather than a generic error. */
export const isUnderReview = (err: unknown) => { const e = errorInfo(err); return e.reason === 'application_under_review' || e.code === 'conflict'; };

// ------------------------------------------------------------------ clients (B01)
export const HEALTH_TONE: Record<HealthBand, Tone> = { healthy: 'good', watch: 'warn', at_risk: 'bad' };
export function clientSummary(c: ClientView) {
  const onboarding = c.status === 'onboarding' || c.health === null;
  const completion = c.deliverables && c.deliverables.total > 0 ? Math.round((c.deliverables.done / c.deliverables.total) * 100) : null;
  return { onboarding, completion, band: c.health?.band ?? null, score: c.health?.score ?? null, reasons: c.health?.reasons ?? [] };
}
/** Allowance exhausted -> upgrade option (B01-002). */
export const isAllowanceExhausted = (err: unknown) => { const e = errorInfo(err); return e.status === 402 || e.reason === 'allowance_exhausted' || e.code === 'payment_required'; };
export const CLIENT_LIST_TABS = ['all', 'onboarding', 'active', 'paused', 'archived'] as const;

// ------------------------------------------------------------------ audit log filter (B01-005)
export function filterAudit<T extends { actorId: string; actorLabel?: string | null }>(entries: readonly T[], actor: string): T[] {
  const a = actor.trim().toLowerCase();
  if (!a) return [...entries];
  return entries.filter((e) => e.actorId.toLowerCase().includes(a) || (e.actorLabel ?? '').toLowerCase().includes(a));
}

// ------------------------------------------------------------------ Meta / Google (B02, B03)
export type MapState = 'mappable' | 'mapped' | 'other_agency' | 'consent_missing';
export function accountMapState(a: { mappedTo: string | null; consent: 'granted' | 'missing' | 'other_agency' }): MapState {
  if (a.consent === 'other_agency') return 'other_agency';
  if (a.consent === 'missing') return 'consent_missing';
  return a.mappedTo ? 'mapped' : 'mappable';
}
export const CONNECTION_TONE: Record<string, Tone> = { connected: 'good', reconnect_needed: 'bad', revoked: 'bad', access_removed: 'bad', not_available: 'warn' };
/** Group proposals per client so each approver only receives its own (B02-002). */
export function groupByClient<T extends { clientTenantId?: string; clientName?: string | null }>(rows: readonly T[]): { clientId: string; clientName: string; rows: T[] }[] {
  const m = new Map<string, { clientId: string; clientName: string; rows: T[] }>();
  for (const r of rows) {
    const id = r.clientTenantId ?? 'unknown';
    const g = m.get(id) ?? { clientId: id, clientName: r.clientName ?? id, rows: [] };
    g.rows.push(r);
    m.set(id, g);
  }
  return [...m.values()];
}

// ------------------------------------------------------------------ WhatsApp usage (B04-002)
export function usageState(u: WaUsageView): { pct: number | null; state: 'ok' | 'near' | 'reached'; aiPct: number | null } {
  const pct = u.messageLimit ? Math.min(100, Math.round((u.messages / u.messageLimit) * 100)) : null;
  const aiPct = u.aiCapMinor ? Math.min(100, Math.round((u.aiCostMinor / u.aiCapMinor) * 100)) : null;
  const top = Math.max(pct ?? 0, aiPct ?? 0);
  return { pct, aiPct, state: u.aiPaused || top >= 100 ? 'reached' : top >= 80 ? 'near' : 'ok' };
}

// ------------------------------------------------------------------ calendar + deliverables (B05)
export function startOfWeek(d: Date): Date {
  const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = (x.getUTCDay() + 6) % 7; // Monday = 0
  x.setUTCDate(x.getUTCDate() - dow);
  return x;
}
export const isoDay = (d: Date) => d.toISOString().slice(0, 10);
export function weekDays(start: Date): string[] {
  return Array.from({ length: 7 }, (_, i) => { const x = new Date(start); x.setUTCDate(x.getUTCDate() + i); return isoDay(x); });
}
const DONE: readonly string[] = ['approved', 'published', 'cancelled', 'done'];
export function isOverdue(item: { dueAt: string | null; state: string; overdue?: boolean }, now: Date): boolean {
  if (item.overdue !== undefined) return item.overdue;
  return !!item.dueAt && Date.parse(item.dueAt) < now.getTime() && !DONE.includes(item.state);
}
export function swimlanes(items: readonly WeekItem[], days: readonly string[]) {
  const lanes = new Map<string, { clientId: string; clientName: string; cells: Record<string, WeekItem[]> }>();
  for (const it of items) {
    const lane = lanes.get(it.clientId) ?? { clientId: it.clientId, clientName: it.clientName, cells: Object.fromEntries(days.map((d) => [d, [] as WeekItem[]])) };
    (lane.cells[it.day] ??= []).push(it);
    lanes.set(it.clientId, lane);
  }
  return [...lanes.values()].sort((a, b) => a.clientName.localeCompare(b.clientName));
}
/** Manual moves offered by the UI: the contract state machine intersected with hand-driven targets. */
export function manualTargets(from: DeliverableState): DeliverableState[] {
  return DELIVERABLE_TRANSITIONS[from].filter((s) => MANUAL_TARGETS.includes(s));
}
export function versionHistory(d: DeliverableView) {
  const versions = [...(d.versions ?? [])].sort((a, b) => b.version - a.version);
  return { current: versions.find((v) => v.version === d.currentVersion) ?? null, history: versions.filter((v) => v.version !== d.currentVersion) };
}
export function publishState(d: Pick<DeliverableView, 'state' | 'publishStatus' | 'publishedUrl' | 'publishAttempts'>): 'not_approved' | 'scheduled' | 'retrying' | 'published' | 'failed' {
  if (d.state === 'published' || d.publishStatus === 'published') return 'published';
  if (d.publishStatus === 'failed') return 'failed';
  if (d.publishStatus === 'retrying') return 'retrying';
  if (d.state !== 'approved') return 'not_approved';
  return 'scheduled';
}
export const MAX_PUBLISH_ATTEMPTS = 3;

// ------------------------------------------------------------------ approvals (B06-003)
const H = 3_600_000;
export function approvalStage(a: Pick<ApprovalView, 'status' | 'requestedAt' | 'remindedAt' | 'escalatedAt'>, now: Date): 'done' | 'waiting' | 'reminded' | 'escalated' {
  if (a.status !== 'pending') return 'done';
  if (a.escalatedAt) return 'escalated';
  if (a.remindedAt) return 'reminded';
  const age = now.getTime() - Date.parse(a.requestedAt);
  return age >= 48 * H ? 'escalated' : age >= 24 * H ? 'reminded' : 'waiting';
}

// ------------------------------------------------------------------ reports (B05-004)
export function partialSections(r: MonthlyReportView) {
  return (r.sections ?? []).filter((s) => s.status === 'partial');
}

// ------------------------------------------------------------------ MCP keys (B07-001)
export function keyStatus(k: Pick<McpKeyView, 'revokedAt' | 'expiresAt'>, now: Date): 'active' | 'revoked' | 'expired' {
  if (k.revokedAt) return 'revoked';
  return Date.parse(k.expiresAt) <= now.getTime() ? 'expired' : 'active';
}
export function toggleScope(scopes: readonly McpScope[], s: McpScope, on: boolean): McpScope[] {
  const set = new Set(scopes);
  if (on) set.add(s); else set.delete(s);
  return MCP_SCOPES.filter((x) => set.has(x));
}
export const isReadOnly = (scopes: readonly string[]) => !scopes.some((s) => s.startsWith('write:'));

// ------------------------------------------------------------------ BYO LLM (B07-003)
export const llmAllowed = (provider: string, model: string) => (AGENCY_LLM_ALLOWLIST[provider] ?? []).includes(model);
export const LLM_PROVIDERS = Object.keys(AGENCY_LLM_ALLOWLIST);

// ------------------------------------------------------------------ white-label (B08)
export interface BrandForm { portalName: string; logoUrl: string; accentColor: string; supportEmail: string }
export function brandProblems(f: BrandForm): { field: keyof BrandForm | 'form'; key: string; suggestion?: string }[] {
  const out: { field: keyof BrandForm | 'form'; key: string; suggestion?: string }[] = [];
  const parsed = BrandSettingsSchema.safeParse({
    ...(f.portalName ? { portalName: f.portalName } : {}), ...(f.logoUrl ? { logoUrl: f.logoUrl } : {}),
    ...(f.accentColor ? { accentColor: f.accentColor } : {}), ...(f.supportEmail ? { supportEmail: f.supportEmail } : {}),
  });
  if (!parsed.success) for (const i of parsed.error.issues) out.push({ field: String(i.path[0]) as keyof BrandForm, key: 'invalid' });
  if (f.accentColor && /^#[0-9a-fA-F]{6}$/.test(f.accentColor)) {
    const c = checkAccent(f.accentColor);
    if (!c.ok) out.push({ field: 'accentColor', key: 'contrast', ...(c.suggestion ? { suggestion: c.suggestion } : {}) });
  }
  if (impersonatesQuixo(f.portalName)) out.push({ field: 'portalName', key: 'impersonation' });
  return out;
}
export function descriptorError(descriptor: string): 'impersonation' | 'invalid_descriptor' | null {
  if (!descriptor) return null;
  return descriptorProblem(descriptor) as 'impersonation' | 'invalid_descriptor' | null;
}
/** Hide "Powered by Quixo OS" needs the white-label add-on (B08-004). */
export const canHideBranding = (b: { whiteLabelAddon: boolean }) => b.whiteLabelAddon;

export type DomainStep = 'add_records' | 'issuing' | 'active' | 'failed';
export function domainStep(d: Pick<DomainView, 'status'>): DomainStep {
  if (d.status === 'active') return 'active';
  if (d.status === 'verification_failed') return 'failed';
  if (d.status === 'pending_ssl') return 'issuing';
  return 'add_records';
}

// ------------------------------------------------------------------ partner home (overview)
/** Health band -> pastel tone. The band label is always rendered next to the tone (colour is never the only signal). */
export const HOME_HEALTH_TONE: Record<HealthBand, PastelTone> = { healthy: 'mint', watch: 'sun', at_risk: 'coral' };
/** Mean of the real per-client health scores, or null when no client is scored yet (no gauge is drawn then). */
export function averageHealth(clients: readonly ClientView[]): number | null {
  const scores = clients.map((c) => c.health?.score).filter((s): s is number => typeof s === 'number');
  return scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
}
export type PortfolioStatus = 'all' | 'onboarding' | 'active' | 'paused' | 'archived';
/** Portfolio counts: the server's tab totals when sent (exact at scale), else counted from the loaded rows. */
export function portfolioCounts(clients: readonly ClientView[], tabs?: Record<string, number>): Record<PortfolioStatus, number> {
  const own = (s: ClientStatus) => clients.filter((c) => c.status === s).length;
  const pick = (k: PortfolioStatus, fallback: number) => (typeof tabs?.[k] === 'number' ? tabs[k]! : fallback);
  return { all: pick('all', clients.length), onboarding: pick('onboarding', own('onboarding')), active: pick('active', own('active')), paused: pick('paused', own('paused')), archived: pick('archived', own('archived')) };
}
export function bandCounts(clients: readonly ClientView[]): Record<HealthBand, number> {
  const n: Record<HealthBand, number> = { healthy: 0, watch: 0, at_risk: 0 };
  for (const c of clients) if (c.health) n[c.health.band] += 1;
  return n;
}
/** Worst-first client order for the home list: at risk, watch, healthy, then unscored. Stable by name. */
export function clientsByAttention(clients: readonly ClientView[]): ClientView[] {
  const rank = (c: ClientView) => (c.health ? { at_risk: 0, watch: 1, healthy: 2 }[c.health.band] : 3);
  return [...clients].filter((c) => c.status !== 'archived').sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}
export type PriorityKind = 'approvals' | 'overdue' | 'at_risk' | 'onboarding' | 'clear';
export interface HomePriority { kind: PriorityKind; count: number; to: string }
/** The one thing to do first: waiting approvals, then overdue work, then at-risk clients, then onboarding clients. */
export function homePriority(i: { approvals: number; overdue: number; atRisk: number; onboarding: number }): HomePriority {
  if (i.approvals > 0) return { kind: 'approvals', count: i.approvals, to: '/approvals' };
  if (i.overdue > 0) return { kind: 'overdue', count: i.overdue, to: '/calendar' };
  if (i.atRisk > 0) return { kind: 'at_risk', count: i.atRisk, to: '/clients?health=at_risk' };
  if (i.onboarding > 0) return { kind: 'onboarding', count: i.onboarding, to: '/clients?status=onboarding' };
  return { kind: 'clear', count: 0, to: '/clients' };
}
export const todayItems = (items: readonly WeekItem[], today: string): WeekItem[] =>
  items.filter((x) => x.day === today).sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? '') || a.title.localeCompare(b.title));
export interface StripDay { day: string; count: number; today: boolean }
export const weekStrip = (items: readonly WeekItem[], days: readonly string[], today: string): StripDay[] =>
  days.map((d) => ({ day: d, count: items.filter((x) => x.day === d).length, today: d === today }));
/** Reports due next: clients with a future next-report date, soonest first. */
export function upcomingReports(clients: readonly ClientView[], now: Date, limit = 4): ClientView[] {
  return clients.filter((c) => c.nextReportAt && Date.parse(c.nextReportAt) >= now.getTime() - 86_400_000)
    .sort((a, b) => Date.parse(a.nextReportAt!) - Date.parse(b.nextReportAt!)).slice(0, limit);
}
